import React, { useEffect, useState } from "react";
import { AdminHeader } from "../Common/CompanyAdminHeader";
import { useLocation } from "react-router-dom";
import { useAuthStore } from "../../stores/auth-store";
import { CreditCard, LucidePlus, Mail, Printer, Users } from "lucide-react";

interface CompanyAdminLayoutProps {
  children: React.ReactNode;
}

interface SelectedPageStatus {
  // 업체 관리
  company: {
    requests: boolean; // 신규 업체 등록
    manage: boolean; // 등록 업체 관리
    withdrawal: boolean; // 운영 종료 신청
  };
  // 장비 관리
  machine: {
    unreported: boolean;
    products: boolean;
    /** 설치 장비 관리 (업체에 놓인 장비 대수 — 등록·별칭·MES 노출) */
    installed: boolean;
  };
  // 사용자 관리
  users: boolean;
  // 결제 관리
  payment: {
    bankTransfer: boolean;
  };
  // 이메일 서명
  emailSignature: boolean;
}

export const Pod24AdminLayout: React.FC<CompanyAdminLayoutProps> = ({
  children,
}) => {
  const location = useLocation();

  // 현재 선택된 페이지를 나타내는데 사용하는 객체
  const selectedPageStatus: SelectedPageStatus = {
    company: {
      requests: location.pathname.includes("/admin/company/requests"),
      manage: location.pathname.includes("/admin/company/manage"),
      withdrawal: location.pathname.includes("/admin/company/withdrawal-requests"),
    },
    machine: {
      unreported: location.pathname.includes(
        "/admin/machine-product/unreported",
      ),
      products:
        location.pathname.includes("/admin/machine-product") &&
        !location.pathname.includes("unreported"),
      installed: location.pathname === "/admin/machine",
    },
    users: location.pathname.includes("/admin/users"),
    payment: {
      bankTransfer: location.pathname.includes("/admin/bank-transfer"),
    },
    emailSignature: location.pathname.includes("/admin/email-signature"),
  };

  // 메뉴 그룹 확장에 사용하는 상태
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

  const handleLogoClick = () => {
    window.location.href = "/";
  };

  const toggleMenu = (menuName: string, forceToggle?: boolean) => {
    setExpandedMenus((prev) => {
      const isExpanded = prev.includes(menuName);

      // If forceToggle is explicitly provided
      if (forceToggle !== undefined) {
        if (forceToggle && !isExpanded) {
          return [...prev, menuName]; // Force open
        } else if (!forceToggle && isExpanded) {
          return prev.filter((item) => item !== menuName); // Force close
        }
        return prev; // No change needed
      }

      // Normal toggle
      return isExpanded
        ? prev.filter((item) => item !== menuName)
        : [...prev, menuName];
    });
  };

  // 자동으로 현재 선택된 페이지가 있는 메뉴 그룹을 확장시킴.
  useEffect(() => {
    if (location.pathname.includes("/admin/company")) {
      toggleMenu("업체", true);
    }
    if (
      location.pathname.includes("/admin/machine-product") ||
      location.pathname === "/admin/machine"
    ) {
      toggleMenu("장비", true);
    }
    if (location.pathname.includes("/admin/bank-transfer")) {
      toggleMenu("결제", true);
    }
  }, [location.pathname]);

  const { user } = useAuthStore();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileMenuSectionEnabled, setIsMobileMenuSectionEnabled] =
    useState(false);

  return (
    <>
      <div
        className={`${isMobileMenuOpen ? "opacity-[1]" : "opacity-0"} transition-all z-[500000] absolute top-0 bottom-0 left-0 right-0 md:hidden ${isMobileMenuSectionEnabled ? "block" : "hidden"}`}
      >
        <div
          className={`${isMobileMenuOpen ? "translate-x-0" : "-translate-x-[199px]"} relative z-[100] transition-all h-screen bg-white shadow-sm border-r border-gray-200 flex flex-col`}
          style={{ width: "200px" }}
        >
          {/* Logo */}
          <div className="pt-[26px] pl-[20px] pr-4 text-left">
            <div className="flex items-center justify-between">
              <div className="w-full">
                <div className="flex justify-between w-full">
                  <div
                    className="font-medium text-lg text-[#252525]"
                    style={{ fontFamily: "Pretendard" }}
                  >
                    {user?.name}
                  </div>
                  <div
                    onClick={async () => {
                      setIsMobileMenuOpen(false);
                      await new Promise((resolve) => {
                        setTimeout(resolve, 500);
                      });
                      setIsMobileMenuSectionEnabled(false);
                    }}
                  >
                    <LucidePlus className="rotate-45 scale-[1.2]" />
                  </div>
                </div>
                <div
                  className="text-xs text-[#555555] mt-1 font-normal"
                  style={{ fontFamily: "Pretendard" }}
                >
                  {user?.email}
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Menu */}
          <nav className="flex-1 pt-8 min-w-[199px]">
            {/* 쇼핑몰 관리 */}
            <div className="mb-[0.7188rem]">
              <button
                onClick={() => toggleMenu("업체")}
                className={`flex items-center justify-between w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPageStatus.company.requests ||
                      selectedPageStatus.company.manage
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    }  rounded relative`}
                style={{ fontFamily: "Pretendard" }}
              >
                {selectedPageStatus.company.requests ||
                selectedPageStatus.company.manage ? (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                ) : null}
                <div className="flex items-center">
                  <img
                    className="ml-[22px] mr-[0.5rem] w-5"
                    src="/assets/icons/Work.svg"
                    alt="쇼핑 가방 아이콘"
                  />
                  업체 관리
                </div>
                <svg
                  className={`h-4 w-4 text-gray-400 transition-transform mr-3 ${
                    expandedMenus.includes("업체") ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {expandedMenus.includes("업체") && (
                <div>
                  <div className="ml-10 mt-1 space-y-1 text-left">
                    <a
                      href="/admin/company/requests"
                      className={`block py-[10px] text-[13px] ${
                        selectedPageStatus.company.requests
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 신규 업체 등록
                    </a>
                    <a
                      href="/admin/company/manage"
                      className={`block py-[10px] text-[13px] ${
                        selectedPageStatus.company.manage
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 등록 업체 관리
                    </a>
                    <a
                      href="/admin/company/withdrawal-requests"
                      className={`block py-[10px] text-[13px] ${
                        selectedPageStatus.company.withdrawal
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 운영 종료 신청
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* 결제 관리 */}
            <div className="mb-[0.7188rem]">
              <button
                onClick={() => toggleMenu("결제")}
                className={`flex items-center justify-between w-full py-[0.7188rem] text-sm font-normal
                  ${
                    selectedPageStatus.payment.bankTransfer
                      ? "bg-[#F5F8FF] text-[#4285F4]"
                      : "text-[#555555] hover:bg-gray-50"
                  }  rounded relative`}
                style={{ fontFamily: "Pretendard" }}
              >
                {selectedPageStatus.payment.bankTransfer ? (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                ) : null}
                <div className="flex items-center">
                  <CreditCard className="ml-[22px] mr-[0.5rem] w-5 h-5" />
                  결제 관리
                </div>
                <svg
                  className={`h-4 w-4 text-gray-400 transition-transform mr-3 ${
                    expandedMenus.includes("결제") ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {expandedMenus.includes("결제") && (
                <div>
                  <div className="ml-10 mt-1 space-y-1 text-left">
                    <a
                      href="/admin/bank-transfer/revenue"
                      className={`block py-[10px] text-[13px] ${
                        selectedPageStatus.payment.bankTransfer
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 무통장입금
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* 장비 관리 */}
            <div className="mb-[0.7188rem]">
              <button
                onClick={() => toggleMenu("장비")}
                className={`flex items-center justify-between w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPageStatus.machine.unreported ||
                      selectedPageStatus.machine.products ||
                      selectedPageStatus.machine.installed
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    }  rounded relative`}
                style={{ fontFamily: "Pretendard" }}
              >
                {selectedPageStatus.machine.unreported ||
                selectedPageStatus.machine.products ||
                selectedPageStatus.machine.installed ? (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                ) : null}
                <div className="flex items-center">
                  <Printer className="ml-[22px] mr-[0.5rem] w-5 h-5" />
                  장비 관리
                </div>
                <svg
                  className={`h-4 w-4 text-gray-400 transition-transform mr-3 ${
                    expandedMenus.includes("장비") ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {expandedMenus.includes("장비") && (
                <div>
                  <div className="ml-10 mt-1 space-y-1 text-left">
                    <a
                      href="/admin/machine-product/unreported"
                      className={`block py-[10px] text-[13px] ${
                        selectedPageStatus.machine.unreported
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 미등록 장비
                    </a>
                    <a
                      href="/admin/machine-product"
                      className={`block py-[10px] text-[13px] ${
                        selectedPageStatus.machine.products
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 장비 OID 관리
                    </a>
                    <a
                      href="/admin/machine"
                      className={`block py-[10px] text-[13px] ${
                        selectedPageStatus.machine.installed
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 설치 장비 관리
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* 사용자 관리 */}
            <div className="mb-[0.7188rem]">
              <a
                href="/admin/users"
                className={`flex items-center w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPageStatus.users
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    } rounded relative`}
                style={{ fontFamily: "Pretendard" }}
              >
                {selectedPageStatus.users ? (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                ) : null}
                <div className="flex items-center">
                  <Users className="ml-[22px] mr-[0.5rem] w-5 h-5" />
                  사용자 관리
                </div>
              </a>
            </div>

            {/* 이메일 서명 */}
            <div className="mb-[0.7188rem]">
              <a
                href="/admin/email-signature"
                className={`flex items-center w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPageStatus.emailSignature
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    } rounded relative`}
                style={{ fontFamily: "Pretendard" }}
              >
                {selectedPageStatus.emailSignature ? (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                ) : null}
                <div className="flex items-center">
                  <Mail className="ml-[22px] mr-[0.5rem] w-5 h-5" />
                  이메일 서명
                </div>
              </a>
            </div>
          </nav>
        </div>
        <div className="absolute bg-black opacity-[0.4] top-0 bottom-0 left-0 right-0"></div>
      </div>

      <div className="min-h-screen bg-gray-50 flex flex-col">
        <AdminHeader
          onLogoClick={handleLogoClick}
          showMyPageLink={true}
          mobileMenuToggleHandler={async () => {
            setIsMobileMenuSectionEnabled(true);
            await new Promise((resolve) => {
              setTimeout(resolve, 200);
            });
            setIsMobileMenuOpen(true);
          }}
        />

        {/* 하단 영역 - 사이드바 + 메인 */}
        <div className="flex flex-1">
          {/* 사이드바 */}
          <div
            className="hidden md:flex bg-white shadow-sm border-r border-gray-200 flex-col"
            style={{ width: "200px" }}
          >
            {/* Logo */}
            <div className="pt-[26px] pl-[20px] pr-4 text-left">
              <div className="flex items-center justify-between">
                <div>
                  <div
                    className="font-medium text-lg text-[#252525]"
                    style={{ fontFamily: "Pretendard" }}
                  >
                    {user?.name}
                  </div>
                  <div
                    className="text-xs text-[#555555] mt-1 font-normal"
                    style={{ fontFamily: "Pretendard" }}
                  >
                    {user?.email}
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Menu */}
            <nav className="flex-1 pt-8 min-w-[199px]">
              {/* 쇼핑몰 관리 */}
              <div className="mb-[0.7188rem]">
                <button
                  onClick={() => toggleMenu("업체")}
                  className={`flex items-center justify-between w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPageStatus.company.requests ||
                      selectedPageStatus.company.manage
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    }  rounded relative`}
                  style={{ fontFamily: "Pretendard" }}
                >
                  {selectedPageStatus.company.requests ||
                  selectedPageStatus.company.manage ? (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                  ) : null}
                  <div className="flex items-center">
                    <img
                      className="ml-[22px] mr-[0.5rem] w-5"
                      src="/assets/icons/Work.svg"
                      alt="쇼핑 가방 아이콘"
                    />
                    업체 관리
                  </div>
                  <svg
                    className={`h-4 w-4 text-gray-400 transition-transform mr-3 ${
                      expandedMenus.includes("업체") ? "rotate-180" : ""
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {expandedMenus.includes("업체") && (
                  <div>
                    <div className="ml-10 mt-1 space-y-1 text-left">
                      <a
                        href="/admin/company/requests"
                        className={`block py-[10px] text-[13px] ${
                          selectedPageStatus.company.requests
                            ? "text-[#4285F4]"
                            : "text-[#757575]"
                        }  hover:text-gray-900 text-left font-normal`}
                      >
                        • 신규 업체 등록
                      </a>
                      <a
                        href="/admin/company/manage"
                        className={`block py-[10px] text-[13px] ${
                          selectedPageStatus.company.manage
                            ? "text-[#4285F4]"
                            : "text-[#757575]"
                        }  hover:text-gray-900 text-left font-normal`}
                      >
                        • 등록 업체 관리
                      </a>
                      <a
                        href="/admin/company/withdrawal-requests"
                        className={`block py-[10px] text-[13px] ${
                          selectedPageStatus.company.withdrawal
                            ? "text-[#4285F4]"
                            : "text-[#757575]"
                        }  hover:text-gray-900 text-left font-normal`}
                      >
                        • 운영 종료 신청
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* 결제 관리 */}
              <div className="mb-[0.7188rem]">
                <button
                  onClick={() => toggleMenu("결제")}
                  className={`flex items-center justify-between w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPageStatus.payment.bankTransfer
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    }  rounded relative`}
                  style={{ fontFamily: "Pretendard" }}
                >
                  {selectedPageStatus.payment.bankTransfer ? (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                  ) : null}
                  <div className="flex items-center">
                    <CreditCard className="ml-[22px] mr-[0.5rem] w-5 h-5" />
                    결제 관리
                  </div>
                  <svg
                    className={`h-4 w-4 text-gray-400 transition-transform mr-3 ${
                      expandedMenus.includes("결제") ? "rotate-180" : ""
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {expandedMenus.includes("결제") && (
                  <div>
                    <div className="ml-10 mt-1 space-y-1 text-left">
                      <a
                        href="/admin/bank-transfer/revenue"
                        className={`block py-[10px] text-[13px] ${
                          selectedPageStatus.payment.bankTransfer
                            ? "text-[#4285F4]"
                            : "text-[#757575]"
                        }  hover:text-gray-900 text-left font-normal`}
                      >
                        • 무통장입금
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* 장비 관리 */}
              <div className="mb-[0.7188rem]">
                <button
                  onClick={() => toggleMenu("장비")}
                  className={`flex items-center justify-between w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPageStatus.machine.unreported ||
                      selectedPageStatus.machine.products ||
                      selectedPageStatus.machine.installed
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    }  rounded relative`}
                  style={{ fontFamily: "Pretendard" }}
                >
                  {selectedPageStatus.machine.unreported ||
                  selectedPageStatus.machine.products ||
                  selectedPageStatus.machine.installed ? (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                  ) : null}
                  <div className="flex items-center">
                    <Printer className="ml-[22px] mr-[0.5rem] w-5 h-5" />
                    장비 관리
                  </div>
                  <svg
                    className={`h-4 w-4 text-gray-400 transition-transform mr-3 ${
                      expandedMenus.includes("장비") ? "rotate-180" : ""
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {expandedMenus.includes("장비") && (
                  <div>
                    <div className="ml-10 mt-1 space-y-1 text-left">
                      <a
                        href="/admin/machine-product/unreported"
                        className={`block py-[10px] text-[13px] ${
                          selectedPageStatus.machine.unreported
                            ? "text-[#4285F4]"
                            : "text-[#757575]"
                        }  hover:text-gray-900 text-left font-normal`}
                      >
                        • 미등록 장비
                      </a>
                      <a
                        href="/admin/machine-product"
                        className={`block py-[10px] text-[13px] ${
                          selectedPageStatus.machine.products
                            ? "text-[#4285F4]"
                            : "text-[#757575]"
                        }  hover:text-gray-900 text-left font-normal`}
                      >
                        • 장비 OID 관리
                      </a>
                      <a
                        href="/admin/machine"
                        className={`block py-[10px] text-[13px] ${
                          selectedPageStatus.machine.installed
                            ? "text-[#4285F4]"
                            : "text-[#757575]"
                        }  hover:text-gray-900 text-left font-normal`}
                      >
                        • 설치 장비 관리
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* 사용자 관리 */}
              <div className="mb-[0.7188rem]">
                <a
                  href="/admin/users"
                  className={`flex items-center w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPageStatus.users
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    } rounded relative`}
                  style={{ fontFamily: "Pretendard" }}
                >
                  {selectedPageStatus.users ? (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                  ) : null}
                  <div className="flex items-center">
                    <Users className="ml-[22px] mr-[0.5rem] w-5 h-5" />
                    사용자 관리
                  </div>
                </a>
              </div>

              {/* 이메일 서명 */}
              <div className="mb-[0.7188rem]">
                <a
                  href="/admin/email-signature"
                  className={`flex items-center w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPageStatus.emailSignature
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    } rounded relative`}
                  style={{ fontFamily: "Pretendard" }}
                >
                  {selectedPageStatus.emailSignature ? (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                  ) : null}
                  <div className="flex items-center">
                    <Mail className="ml-[22px] mr-[0.5rem] w-5 h-5" />
                    이메일 서명
                  </div>
                </a>
              </div>
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col overflow-x-scroll">
            {/* Content Area — .App 전역 text-align:center 상속을 차단해 어드민은 항상 왼쪽 정렬 */}
            <main className="p-5 text-left">{children}</main>
          </div>
        </div>
      </div>
    </>
  );
};
